import React, { useEffect, useState } from "react";
import axios from "axios";
import { useToast } from "@/hooks/use-toast";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const PRESET_COLORS = [
  "#10b981", "#3b82f6", "#6366f1", "#8b5cf6", "#ec4899",
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#14b8a6"
];

export function TrainingSectionsConfig({ authToken }) {
  const { toast } = useToast();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [newSection, setNewSection] = useState({
    name: "",
    key: "",
    color: "#10b981",
    has_certificate: true,
    exercises: []
  });

  const [newExercise, setNewExercise] = useState({ label: "" });

  function authHeaders() {
    return authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {};
  }

  async function loadSections() {
    try {
      const res = await axios.get(`${API_BASE}/api/training-sections`, authHeaders());
      setSections(res.data || []);
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Laden", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSections();
  }, [authToken]);

  const generateKey = (name) => {
    return name.toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss")
      .replace(/[^a-z0-9_]/g, "");
  };

  const createSection = async () => {
    if (!newSection.name.trim()) {
      toast({ title: "Bitte Namen eingeben", variant: "destructive" });
      return;
    }
    try {
      const key = newSection.key || generateKey(newSection.name);
      const res = await axios.post(`${API_BASE}/api/training-sections`, {
        ...newSection,
        key,
        exercises: newSection.exercises.map((ex, i) => ({
          key: generateKey(ex.label),
          label: ex.label,
          order: i
        }))
      }, authHeaders());
      setSections([...sections, res.data]);
      setNewSection({ name: "", key: "", color: "#10b981", has_certificate: true, exercises: [] });
      setShowNewForm(false);
      toast({ title: `Abschnitt "${res.data.name}" angelegt` });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Fehler beim Anlegen";
      toast({ title: msg, variant: "destructive" });
    }
  };

  const updateSection = async (sectionKey) => {
    if (!editingSection) return;
    try {
      const res = await axios.put(`${API_BASE}/api/training-sections/${sectionKey}`, {
        name: editingSection.name,
        color: editingSection.color,
        has_certificate: editingSection.has_certificate,
        exercises: editingSection.exercises.map((ex, i) => ({
          key: ex.key || generateKey(ex.label),
          label: ex.label,
          order: i
        }))
      }, authHeaders());
      setSections(sections.map(s => s.key === sectionKey ? res.data : s));
      setEditingSection(null);
      toast({ title: "Abschnitt aktualisiert" });
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Speichern", variant: "destructive" });
    }
  };

  const deleteSection = async (sectionKey) => {
    try {
      await axios.delete(`${API_BASE}/api/training-sections/${sectionKey}`, authHeaders());
      setSections(sections.filter(s => s.key !== sectionKey));
      setDeleteConfirm(null);
      toast({ title: "Abschnitt gelöscht" });
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Löschen", variant: "destructive" });
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Standard-Abschnitte (Seepferdchen, Freischwimmer) wiederherstellen?")) return;
    try {
      const res = await axios.post(`${API_BASE}/api/training-sections/reset-defaults`, {}, authHeaders());
      setSections(res.data);
      toast({ title: "Standard-Abschnitte wiederhergestellt" });
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler", variant: "destructive" });
    }
  };

  const addExercise = (target, setTarget) => {
    if (!newExercise.label.trim()) return;
    setTarget({
      ...target,
      exercises: [...target.exercises, { key: generateKey(newExercise.label), label: newExercise.label, order: target.exercises.length }]
    });
    setNewExercise({ label: "" });
  };

  const removeExercise = (target, setTarget, index) => {
    setTarget({
      ...target,
      exercises: target.exercises.filter((_, i) => i !== index)
    });
  };

  const getTextColor = (bgColor) => {
    if (!bgColor?.startsWith("#")) return "#ffffff";
    try {
      const r = parseInt(bgColor.slice(1, 3), 16);
      const g = parseInt(bgColor.slice(3, 5), 16);
      const b = parseInt(bgColor.slice(5, 7), 16);
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? "#1f2937" : "#ffffff";
    } catch {
      return "#ffffff";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-violet-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="training-sections-config">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-white p-4 shadow-sm">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Leistungsstand konfigurieren</h3>
          <p className="text-sm text-slate-500">
            {sections.length} Abschnitte · Übungen und Zeugnisse verwalten
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={resetDefaults}
            className="rounded-full bg-slate-100 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-200"
          >
            Standards wiederherstellen
          </button>
          <button
            data-testid="new-section-button"
            onClick={() => setShowNewForm(!showNewForm)}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${
              showNewForm ? "bg-slate-900 text-white" : "bg-violet-100 text-violet-700 hover:bg-violet-200"
            }`}
          >
            {showNewForm ? "Abbrechen" : "+ Neuer Abschnitt"}
          </button>
        </div>
      </div>

      {/* Lösch-Bestätigung */}
      {deleteConfirm && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-rose-900">Abschnitt löschen?</h4>
              <p className="mt-1 text-xs text-rose-700">
                Möchten Sie <strong>"{deleteConfirm.name}"</strong> wirklich löschen?
                Bereits eingetragene Bewertungen bleiben erhalten.
              </p>
              <div className="mt-3 flex gap-2">
                <button onClick={() => setDeleteConfirm(null)} className="rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50">
                  Abbrechen
                </button>
                <button onClick={() => deleteSection(deleteConfirm.key)} className="rounded-full bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-500">
                  Ja, löschen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Neuer Abschnitt Form */}
      {showNewForm && (
        <div className="rounded-xl bg-gradient-to-br from-violet-50 to-white p-4 shadow-sm">
          <h4 className="mb-3 text-sm font-semibold text-violet-900">Neuen Abschnitt anlegen</h4>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs text-slate-500">Name *</label>
                <input
                  type="text"
                  value={newSection.name}
                  onChange={(e) => setNewSection({ ...newSection, name: e.target.value })}
                  placeholder="z.B. Bronze-Abzeichen"
                  className="w-full rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Farbe</label>
                <div className="flex gap-1">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setNewSection({ ...newSection, color })}
                      className={`h-8 w-8 rounded-lg transition-all ${newSection.color === color ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newSection.has_certificate}
                  onChange={(e) => setNewSection({ ...newSection, has_certificate: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-violet-600"
                />
                <span className="text-sm text-slate-700">Zeugnis kann vergeben werden</span>
              </label>
            </div>

            <div>
              <label className="mb-2 block text-xs text-slate-500">Übungen</label>
              <div className="space-y-2">
                {newSection.exercises.map((ex, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg bg-white p-2 shadow-sm">
                    <span className="flex-1 text-sm text-slate-700">{ex.label}</span>
                    <button
                      onClick={() => removeExercise(newSection, setNewSection, i)}
                      className="text-slate-400 hover:text-rose-500"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newExercise.label}
                    onChange={(e) => setNewExercise({ label: e.target.value })}
                    onKeyPress={(e) => e.key === "Enter" && addExercise(newSection, setNewSection)}
                    placeholder="Übung hinzufügen..."
                    className="flex-1 rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                  <button
                    onClick={() => addExercise(newSection, setNewSection)}
                    className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-600 hover:bg-slate-200"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            <button
              onClick={createSection}
              className="rounded-full bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500"
            >
              Abschnitt anlegen
            </button>
          </div>
        </div>
      )}

      {/* Abschnitte Liste */}
      <div className="space-y-3">
        {sections.length === 0 ? (
          <div className="rounded-xl bg-white p-8 text-center shadow-sm">
            <p className="text-sm text-slate-500">Keine Abschnitte konfiguriert.</p>
            <button onClick={resetDefaults} className="mt-2 text-sm text-violet-600 hover:underline">
              Standard-Abschnitte laden
            </button>
          </div>
        ) : (
          sections.map((section) => (
            <div
              key={section.key}
              data-testid={`section-${section.key}`}
              className="rounded-xl bg-white shadow-sm overflow-hidden"
            >
              {/* Header */}
              <div
                className="px-4 py-3 flex items-center justify-between"
                style={{ backgroundColor: section.color, color: getTextColor(section.color) }}
              >
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{section.name}</span>
                  {section.is_default && (
                    <span className="rounded bg-white/20 px-1.5 py-0.5 text-[10px]">Standard</span>
                  )}
                  {section.has_certificate && (
                    <span className="rounded bg-white/20 px-1.5 py-0.5 text-[10px]">Zeugnis</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingSection(editingSection?.key === section.key ? null : { ...section })}
                    className="rounded px-2 py-1 text-xs bg-white/20 hover:bg-white/30"
                  >
                    {editingSection?.key === section.key ? "Schließen" : "Bearbeiten"}
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(section)}
                    className="rounded px-2 py-1 text-xs bg-white/20 hover:bg-rose-500"
                  >
                    Löschen
                  </button>
                </div>
              </div>

              {/* Bearbeiten */}
              {editingSection?.key === section.key ? (
                <div className="p-4 space-y-4 bg-slate-50">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs text-slate-500">Name</label>
                      <input
                        type="text"
                        value={editingSection.name}
                        onChange={(e) => setEditingSection({ ...editingSection, name: e.target.value })}
                        className="w-full rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-slate-500">Farbe</label>
                      <div className="flex gap-1">
                        {PRESET_COLORS.map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setEditingSection({ ...editingSection, color })}
                            className={`h-6 w-6 rounded transition-all ${editingSection.color === color ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingSection.has_certificate}
                      onChange={(e) => setEditingSection({ ...editingSection, has_certificate: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-violet-600"
                    />
                    <span className="text-sm text-slate-700">Zeugnis kann vergeben werden</span>
                  </label>

                  <div>
                    <label className="mb-2 block text-xs text-slate-500">Übungen ({editingSection.exercises.length})</label>
                    <div className="space-y-2">
                      {editingSection.exercises.map((ex, i) => (
                        <div key={i} className="flex items-center gap-2 rounded-lg bg-white p-2 shadow-sm">
                          <input
                            type="text"
                            value={ex.label}
                            onChange={(e) => {
                              const newExercises = [...editingSection.exercises];
                              newExercises[i] = { ...ex, label: e.target.value };
                              setEditingSection({ ...editingSection, exercises: newExercises });
                            }}
                            className="flex-1 text-sm text-slate-700 bg-transparent focus:outline-none"
                          />
                          <button
                            onClick={() => removeExercise(editingSection, setEditingSection, i)}
                            className="text-slate-400 hover:text-rose-500"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newExercise.label}
                          onChange={(e) => setNewExercise({ label: e.target.value })}
                          onKeyPress={(e) => e.key === "Enter" && addExercise(editingSection, setEditingSection)}
                          placeholder="Übung hinzufügen..."
                          className="flex-1 rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                        />
                        <button
                          onClick={() => addExercise(editingSection, setEditingSection)}
                          className="rounded-lg bg-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-300"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => updateSection(section.key)}
                    className="rounded-full bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500"
                  >
                    Änderungen speichern
                  </button>
                </div>
              ) : (
                /* Übungen Übersicht */
                <div className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {section.exercises.map((ex) => (
                      <span key={ex.key} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">
                        {ex.label}
                      </span>
                    ))}
                    {section.exercises.length === 0 && (
                      <span className="text-xs text-slate-400">Keine Übungen definiert</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
